import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { SpriteComponent } from '../components/SpriteComponent';
import { worldToScreen, calculateDepth, TILE_WIDTH, TILE_HEIGHT } from '../../utils/IsometricUtils';

/**
 * RenderSystem - Synchronizes Phaser sprites with ECS components
 * Handles position updates, depth sorting, and visual state
 */
export class RenderSystem extends System {
  public readonly requiredComponents = ['transform', 'sprite'];

  /** Offset to center the map on screen */
  private mapOffsetX: number = 0;
  private mapOffsetY: number = 0;

  /** Reference to tilemap layer for coordinate conversion */
  private groundLayer: Phaser.Tilemaps.TilemapLayer | null = null;

  /** Debug graphics for collision visualization */
  private debugGraphics: Phaser.GameObjects.Graphics | null = null;
  private showCollisionRadius: boolean = false;
  private collisionRadius: number = 0.15;

  /** Collision layer tile ID that blocks movement (must match TilemapManager) */
  private static readonly COLLISION_BLOCK = 1;

  /** Reference to collision layer for debug visualization */
  private collisionLayer: Phaser.Tilemaps.TilemapLayer | null = null;

  public init(): void {
    // Calculate initial map offset to center on screen
    const camera = this.world.getScene().cameras.main;
    this.mapOffsetX = camera.width / 2;
    this.mapOffsetY = 200; // Offset from top
  }

  /**
   * Set the map offset for centering (used when no tilemap)
   */
  public setMapOffset(x: number, y: number): void {
    this.mapOffsetX = x;
    this.mapOffsetY = y;
  }

  /**
   * Set tilemap layer for coordinate conversion
   * When set, entities will be positioned using Phaser's tile coordinate system
   */
  public setTilemap(_tilemap: Phaser.Tilemaps.Tilemap | null, groundLayer?: Phaser.Tilemaps.TilemapLayer | null, collisionLayer?: Phaser.Tilemaps.TilemapLayer | null): void {
    this.groundLayer = groundLayer || null;
    this.collisionLayer = collisionLayer || null;
    if (this.groundLayer) {
      console.log('RenderSystem: Using tilemap for coordinate conversion');
    }
  }

  /**
   * Enable/disable collision radius visualization for player
   */
  public setShowCollisionRadius(show: boolean, radius?: number): void {
    this.showCollisionRadius = show;
    if (radius !== undefined) {
      this.collisionRadius = radius;
    }

    if (show && !this.debugGraphics) {
      this.debugGraphics = this.world.getScene().add.graphics();
      this.debugGraphics.setDepth(1000);
    } else if (!show && this.debugGraphics) {
      this.debugGraphics.destroy();
      this.debugGraphics = null;
    }
  }

  public update(entities: Entity[], _delta: number): void {
    // Clear debug graphics each frame
    if (this.debugGraphics) {
      this.debugGraphics.clear();
    }

    // Draw blocking tile outlines when debug is enabled
    if (this.showCollisionRadius && this.debugGraphics) {
      this.drawBlockingTiles();
    }

    for (const entity of entities) {
      this.syncEntity(entity);

      // Draw collision radius for player
      if (this.showCollisionRadius && this.debugGraphics && entity.hasTag('player')) {
        this.drawCollisionRadius(entity);
      }
    }
  }

  /**
   * Draw collision radius visualization for an entity
   * Shows all 9 points that are actually tested for collision (matching TilemapManager.isWalkable)
   * Also highlights the tiles that each point is checking
   */
  private drawCollisionRadius(entity: Entity): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    if (!transform || !this.debugGraphics) return;

    const radius = this.collisionRadius;

    // These are the exact 9 points checked by TilemapManager.isWalkable
    const checkPoints = [
      { x: transform.x, y: transform.y },                   // center
      { x: transform.x - radius, y: transform.y - radius }, // top-left
      { x: transform.x + radius, y: transform.y - radius }, // top-right
      { x: transform.x - radius, y: transform.y + radius }, // bottom-left
      { x: transform.x + radius, y: transform.y + radius }, // bottom-right
      { x: transform.x - radius, y: transform.y },          // left
      { x: transform.x + radius, y: transform.y },          // right
      { x: transform.x, y: transform.y - radius },          // top
      { x: transform.x, y: transform.y + radius },          // bottom
    ];

    // Highlight tiles being checked (semi-transparent cyan)
    // Uses same geometric point-in-diamond test as TilemapManager.isPointWalkable
    const checkedTiles = new Set<string>();
    this.debugGraphics.fillStyle(0x00ffff, 0.3);
    for (const point of checkPoints) {
      // Get screen position of this check point (matching entity rendering)
      const screenPos = this.worldToScreenPos(point.x, point.y, 0);

      const baseTileX = Math.floor(point.x);
      const baseTileY = Math.floor(point.y);

      // Check all 4 tiles around the point
      const tilesToCheck = [
        { x: baseTileX, y: baseTileY },
        { x: baseTileX + 1, y: baseTileY },
        { x: baseTileX, y: baseTileY + 1 },
        { x: baseTileX + 1, y: baseTileY + 1 },
      ];

      for (const tile of tilesToCheck) {
        // Check if screen point is inside this tile's diamond
        if (!this.isPointInTileDiamond(screenPos.x, screenPos.y, tile.x, tile.y)) {
          continue;
        }

        const key = `${tile.x},${tile.y}`;
        if (!checkedTiles.has(key)) {
          checkedTiles.add(key);
          this.drawTileHighlight(tile.x, tile.y);
        }
      }
    }

    const screenPoints = checkPoints.map((c) => this.worldToScreenPos(c.x, c.y, 0));

    // Draw the collision box outline (connecting the 4 corners)
    const cornerIndices = [1, 2, 4, 3]; // top-left, top-right, bottom-right, bottom-left
    this.debugGraphics.lineStyle(2, 0xff6b35, 0.9); // Orange
    this.debugGraphics.beginPath();
    this.debugGraphics.moveTo(screenPoints[cornerIndices[0]].x, screenPoints[cornerIndices[0]].y);
    for (let i = 1; i < cornerIndices.length; i++) {
      this.debugGraphics.lineTo(screenPoints[cornerIndices[i]].x, screenPoints[cornerIndices[i]].y);
    }
    this.debugGraphics.closePath();
    this.debugGraphics.strokePath();

    // Draw center point (white)
    this.debugGraphics.fillStyle(0xffffff, 0.9);
    this.debugGraphics.fillCircle(screenPoints[0].x, screenPoints[0].y, 3);

    // Draw corner points (orange)
    this.debugGraphics.fillStyle(0xff6b35, 0.9);
    for (let i = 1; i <= 4; i++) {
      this.debugGraphics.fillCircle(screenPoints[i].x, screenPoints[i].y, 2);
    }

    // Draw midpoint check points (light orange)
    this.debugGraphics.fillStyle(0xffaa80, 0.7);
    for (let i = 5; i <= 8; i++) {
      this.debugGraphics.fillCircle(screenPoints[i].x, screenPoints[i].y, 2);
    }
  }

  /**
   * Draw a filled highlight for a tile (used to show which tiles are being collision-checked)
   */
  private drawTileHighlight(tileX: number, tileY: number): void {
    if (!this.debugGraphics || !this.groundLayer) return;

    const pos = this.groundLayer.tileToWorldXY(tileX, tileY);
    if (!pos) return;

    const halfW = TILE_WIDTH / 2;
    const halfH = TILE_HEIGHT / 2;

    this.debugGraphics.beginPath();
    this.debugGraphics.moveTo(pos.x + halfW, pos.y);
    this.debugGraphics.lineTo(pos.x + TILE_WIDTH, pos.y + halfH);
    this.debugGraphics.lineTo(pos.x + halfW, pos.y + TILE_HEIGHT);
    this.debugGraphics.lineTo(pos.x, pos.y + halfH);
    this.debugGraphics.closePath();
    this.debugGraphics.fillPath();
  }

  /**
   * Check if a screen point is inside a tile's isometric diamond
   * (Same logic as TilemapManager.isPointInTileDiamond)
   */
  private isPointInTileDiamond(screenX: number, screenY: number, tileX: number, tileY: number): boolean {
    if (!this.groundLayer) return false;

    const tilePos = this.groundLayer.tileToWorldXY(tileX, tileY);
    if (!tilePos) return false;

    // Diamond vertices
    const top = { x: tilePos.x + TILE_WIDTH / 2, y: tilePos.y };
    const right = { x: tilePos.x + TILE_WIDTH, y: tilePos.y + TILE_HEIGHT / 2 };
    const bottom = { x: tilePos.x + TILE_WIDTH / 2, y: tilePos.y + TILE_HEIGHT };
    const left = { x: tilePos.x, y: tilePos.y + TILE_HEIGHT / 2 };

    // Check if point is inside the diamond using cross product signs
    const d1 = this.crossProductSign(screenX, screenY, top.x, top.y, right.x, right.y);
    const d2 = this.crossProductSign(screenX, screenY, right.x, right.y, bottom.x, bottom.y);
    const d3 = this.crossProductSign(screenX, screenY, bottom.x, bottom.y, left.x, left.y);
    const d4 = this.crossProductSign(screenX, screenY, left.x, left.y, top.x, top.y);

    const hasNeg = (d1 < 0) || (d2 < 0) || (d3 < 0) || (d4 < 0);
    const hasPos = (d1 > 0) || (d2 > 0) || (d3 > 0) || (d4 > 0);

    return !(hasNeg && hasPos);
  }

  /**
   * Cross product sign for point-in-polygon testing
   */
  private crossProductSign(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    return (px - x2) * (y1 - y2) - (x1 - x2) * (py - y2);
  }

  /**
   * Draw outlines for all blocking tiles (from collision layer)
   */
  private drawBlockingTiles(): void {
    if (!this.debugGraphics) return;

    // Use collision layer for blocking tiles (separation of visuals and collision)
    const layer = this.collisionLayer || this.groundLayer;
    if (!layer) return;

    const mapWidth = layer.layer.width;
    const mapHeight = layer.layer.height;

    this.debugGraphics.lineStyle(2, 0xff0000, 0.8);

    for (let tileY = 0; tileY < mapHeight; tileY++) {
      for (let tileX = 0; tileX < mapWidth; tileX++) {
        const tile = layer.getTileAt(tileX, tileY);
        if (tile && tile.index === RenderSystem.COLLISION_BLOCK) {
          this.drawTileOutline(tileX, tileY);
        }
      }
    }
  }

  /**
   * Draw an isometric diamond outline for a tile
   */
  private drawTileOutline(tileX: number, tileY: number): void {
    if (!this.debugGraphics || !this.groundLayer) return;

    // Get the screen position where Phaser draws this tile
    const pos = this.groundLayer.tileToWorldXY(tileX, tileY);
    if (!pos) return;

    // For an isometric tile, the diamond vertices are:
    // - Top: center-top of the tile's bounding box
    // - Right: center-right
    // - Bottom: center-bottom
    // - Left: center-left
    const halfW = TILE_WIDTH / 2;
    const halfH = TILE_HEIGHT / 2;

    this.debugGraphics.beginPath();
    this.debugGraphics.moveTo(pos.x + halfW, pos.y);                  // top vertex
    this.debugGraphics.lineTo(pos.x + TILE_WIDTH, pos.y + halfH);     // right vertex
    this.debugGraphics.lineTo(pos.x + halfW, pos.y + TILE_HEIGHT);    // bottom vertex
    this.debugGraphics.lineTo(pos.x, pos.y + halfH);                  // left vertex
    this.debugGraphics.closePath();
    this.debugGraphics.strokePath();
  }

  /**
   * Convert world grid coordinates to screen position
   * Uses Phaser's tilemap methods when available for proper alignment
   */
  private worldToScreenPos(worldX: number, worldY: number, worldZ: number = 0): { x: number; y: number } {
    // Use tilemap's coordinate system if available
    if (this.groundLayer) {
      // tileToWorldXY converts tile coords to the tile's top-left corner in world space
      const pos = this.groundLayer.tileToWorldXY(worldX, worldY);
      if (pos) {
        // Offset to center of tile
        return {
          x: pos.x + TILE_WIDTH / 2,
          y: pos.y + TILE_HEIGHT / 2 - worldZ,
        };
      }
    }

    // Fallback to manual calculation
    const screenPos = worldToScreen(worldX, worldY, worldZ);
    return {
      x: screenPos.x + this.mapOffsetX,
      y: screenPos.y + this.mapOffsetY,
    };
  }

  /**
   * Synchronize a single entity's sprite with its transform
   */
  private syncEntity(entity: Entity): void {
    const transform = entity.getComponent<TransformComponent>('transform');
    const sprite = entity.getComponent<SpriteComponent>('sprite');

    if (!transform || !sprite) return;

    // Create sprite if it doesn't exist
    if (!sprite.sprite && sprite.textureKey) {
      this.createSprite(entity, sprite, transform);
    }

    if (!sprite.sprite) return;

    // Convert world coordinates to screen coordinates
    const screenPos = this.worldToScreenPos(transform.x, transform.y, transform.z);

    // Update sprite position
    sprite.sprite.setPosition(screenPos.x, screenPos.y);

    // Calculate and apply depth for isometric sorting
    const depth = calculateDepth(transform.x, transform.y, transform.z);
    sprite.sprite.setDepth(depth);
    sprite.depth = depth;

    // Sync visual properties
    sprite.sprite.setScale(sprite.scaleX, sprite.scaleY);
    sprite.sprite.setAlpha(sprite.alpha);
    sprite.sprite.setVisible(sprite.visible);

    if (sprite.tint !== 0xffffff) {
      sprite.sprite.setTint(sprite.tint);
    }
  }

  /**
   * Create a Phaser sprite for an entity
   */
  private createSprite(
    entity: Entity,
    sprite: SpriteComponent,
    transform: TransformComponent
  ): void {
    const scene = this.world.getScene();

    // Check if texture exists
    if (!scene.textures.exists(sprite.textureKey)) {
      console.warn(`Texture '${sprite.textureKey}' not found for entity ${entity.id}`);
      return;
    }

    // Calculate initial screen position using consistent method
    const screenPos = this.worldToScreenPos(transform.x, transform.y, transform.z);

    // Create the sprite
    sprite.sprite = scene.add.sprite(screenPos.x, screenPos.y, sprite.textureKey);

    // Set origin to bottom center for better isometric placement
    sprite.sprite.setOrigin(0.5, 0.8);

    // Apply initial depth
    const depth = calculateDepth(transform.x, transform.y, transform.z);
    sprite.sprite.setDepth(depth);
    sprite.depth = depth;

    // Apply initial visual properties
    sprite.sprite.setScale(sprite.scaleX, sprite.scaleY);
    sprite.sprite.setAlpha(sprite.alpha);
    sprite.sprite.setVisible(sprite.visible);
  }

  public onEntityAdded(entity: Entity): void {
    const sprite = entity.getComponent<SpriteComponent>('sprite');
    const transform = entity.getComponent<TransformComponent>('transform');

    if (sprite && transform && !sprite.sprite && sprite.textureKey) {
      this.createSprite(entity, sprite, transform);
    }
  }

  public onEntityRemoved(entity: Entity): void {
    const sprite = entity.getComponent<SpriteComponent>('sprite');

    if (sprite?.sprite) {
      sprite.sprite.destroy();
      sprite.sprite = null;
    }
  }

  public destroy(): void {
    // Destroy all managed sprites
    const entities = this.world.query(this.requiredComponents);
    for (const entity of entities) {
      const sprite = entity.getComponent<SpriteComponent>('sprite');
      if (sprite?.sprite) {
        sprite.sprite.destroy();
        sprite.sprite = null;
      }
    }

    // Clean up debug graphics
    if (this.debugGraphics) {
      this.debugGraphics.destroy();
      this.debugGraphics = null;
    }
  }
}
